// Description: Global state management (Domain + UI only)
// Updated: 03/2026 – Auth fully removed from Zustand
// Author: Simon Franklin

import { create } from "zustand";
import { ProfileGet } from "../services/api/Profile/ProfileGet";
import AlternativeAvatar from "../shared/assets/images/avatar-padrao.png";
import { parseFeatureAccessFromProfile } from "../shared/utils/featureAccess";

const PLAN_STORAGE_KEY = "app:planoAtual";
const FEATURE_ACCESS_STORAGE_KEY = "app:featureAccess";

function getInitialPlan() {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(PLAN_STORAGE_KEY);
}

function persistPlan(plan) {
  if (typeof window === "undefined") return;

  if (plan) {
    sessionStorage.setItem(PLAN_STORAGE_KEY, plan);
    return;
  }

  sessionStorage.removeItem(PLAN_STORAGE_KEY);
}

function getInitialFeatureAccess() {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(FEATURE_ACCESS_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch (err) {
    console.warn("Não foi possível recuperar feature access do storage:", err);
  }

  return null;
}

function persistFeatureAccess(featureAccess) {
  if (typeof window === "undefined") return;

  if (featureAccess && typeof featureAccess === "object") {
    sessionStorage.setItem(
      FEATURE_ACCESS_STORAGE_KEY,
      JSON.stringify(featureAccess),
    );
    return;
  }

  sessionStorage.removeItem(FEATURE_ACCESS_STORAGE_KEY);
}

const use123zapStore = create((set, get) => ({
  // =====================================================
  // DOMAIN STATE
  // =====================================================

  templates: [],
  setTemplates: (value) => set({ templates: value }),
  alreadyFetchTemplates: false,
  setAlreadyFetchTemplates: (value) => set({ alreadyFetchTemplates: value }),

  channels: [],
  setChannels: (value) => set({ channels: value }),
  alreadyFetchChannels: false,
  setAlreadyFetchChannels: (value) => set({ alreadyFetchChannels: value }),

  campaigns: [],
  setCampaigns: (value) => set({ campaigns: value }),
  alreadyFetchCampaigns: false,
  setAlreadyFetchCampaigns: (value) => set({ alreadyFetchCampaigns: value }),

  sheets: [],
  setSheets: (value) => set({ sheets: value }),
  alreadyFetchSheets: false,
  setAlreadyFetchSheets: (value) => set({ alreadyFetchSheets: value }),

  integrations: [],
  setIntegrations: (value) => set({ integrations: value }),
  alreadyFetchIntegrations: false,
  setAlreadyFetchIntegrations: (value) =>
    set({ alreadyFetchIntegrations: value }),

  registeredERPS: [],
  setRegisteredERPS: (value) => set({ registeredERPS: value }),
  alreadyFetchRegisteredERPS: false,
  setAlreadyFetchRegisteredERPS: (value) =>
    set({ alreadyFetchRegisteredERPS: value }),

  planoAtual: getInitialPlan(),
  setPlanoAtual: (value) => {
    persistPlan(value);
    set({ planoAtual: value });
  },
  clearPlanoAtual: () => {
    persistPlan(null);
    set({ planoAtual: null });
  },
  featureAccess: getInitialFeatureAccess(),
  setFeatureAccess: (value) => {
    persistFeatureAccess(value);
    set({ featureAccess: value });
  },
  clearFeatureAccess: () => {
    persistFeatureAccess(null);
    set({ featureAccess: null });
  },

  // =====================================================
  // PROFILE (não é auth, é domínio)
  // =====================================================

  userAvatar: AlternativeAvatar,
  setUserAvatar: (value) => set({ userAvatar: value }),

  loadPlanoAtual: async (force = false) => {
    const { planoAtual } = get();

    console.log("loadPlanoAtual chamado");

    if (force || !planoAtual) {
      try {
        const response = await ProfileGet();
        console.log("Plano retornado:", response.plan_name);
        const parsedFeatureAccess = parseFeatureAccessFromProfile(response);

        persistPlan(response.plan_name);
        persistFeatureAccess(parsedFeatureAccess);
        set({
          planoAtual: response.plan_name,
          featureAccess: parsedFeatureAccess,
        });
      } catch (err) {
        console.error("Erro ao carregar plano atual:", err);
      }
    }
  },

  // =====================================================
  // DASHBOARD STATE
  // =====================================================

  dashboardSentiments: null,
  dashboardTotalMessages: 0,
  dashboardLoading: false,
  dashboardLoaded: false,

  setDashboardData: (sentiments, total) =>
    set({
      dashboardSentiments: sentiments,
      dashboardTotalMessages: total,
      dashboardLoaded: true,
    }),

  setDashboardLoading: (value) => set({ dashboardLoading: value }),

  resetDashboard: () =>
    set({
      dashboardLoaded: false,
    }),

  // =====================================================
  // AI ANALYSIS STATE
  // =====================================================

  aiAnalysisText: "",
  aiAnalysisLoading: false,
  aiAnalysisGenerated: false,

  setAiAnalysis: (text) =>
    set({
      aiAnalysisText: text,
      aiAnalysisGenerated: true,
    }),

  setAiAnalysisLoading: (value) => set({ aiAnalysisLoading: value }),

  resetAiAnalysis: () =>
    set({
      aiAnalysisText: "",
      aiAnalysisGenerated: false,
    }),

  // =====================================================
  // UI STATE
  // =====================================================

  finishedRegister: null,
  setFinishedRegister: (value) => set({ finishedRegister: value }),

  isCollapsed: false,
  setIsCollapsed: (value) => set({ isCollapsed: value }),

  showSidebar: false,
  setShowSidebar: (value) => set({ showSidebar: value }),

  // =====================================================
  // TABLE / REPORT STATE
  // =====================================================

  tableColumns: [
    "Título da campanha",
    "Modelo utilizado",
    "Destinatário",
    "Data de envio",
    "Status/Entrega",
  ],
  setTableColumns: (value) => set({ tableColumns: value }),

  campaignsSummaryData: [],
  setCampaignsSummaryData: (value) => set({ campaignsSummaryData: value }),

  excelTableRows: [],
  setExcelTableRows: (value) => set({ excelTableRows: value }),

  excelTableColumns: [],
  setExcelTableColumns: (value) => set({ excelTableColumns: value }),

  showNewTable: false,
  setShowNewTable: (value) => set({ showNewTable: value }),

  receivedMessages: {
    columns: [],
    rows: [],
  },
  setReceivedMessages: (value) => set({ receivedMessages: value }),

  showReceived: false,
  setShowReceived: (value) => set({ showReceived: value }),

  modalData: { label: "", date: "" },
  setModalData: (value) => set({ modalData: value }),
}));

export default use123zapStore;
